/**
 * warmClip — download a clip ahead of its first hearing and KEEP the bytes
 * (job #298).
 *
 * The warm-ups (the first clip before ready, the next cycle's voices during
 * the pause) used to fetch and discard, trusting the browser's HTTP cache to
 * serve the <audio> element. WebKit's media loader does not read that cache:
 * the element downloaded the clip again after play(), a 0-1 probe then the
 * full body, 190-450 ms of silence on the very first sound. Handing the bytes
 * to AudioCache.adoptBytes means the clip plays as a local WAV blob through
 * the resolver every learner already plays through, and the cache-ahead
 * stores the same bytes rather than downloading them a third time.
 *
 * Resolves once the body has landed (or the fetch failed); never rejects.
 */
import { getAudioCache } from './createAudioCache'
import { bootClipPending } from '../playback/signedInBoot'

const AUDIO_ID = /\/api\/audio\/([0-9a-f-]{36})(?:[?#]|$)/i
const pendingWarmups = new Map<string, Promise<void>>()

export function audioIdOfUrl(url: string): string | null {
  return url.match(AUDIO_ID)?.[1] ?? null
}

export function warmClip(
  url: string,
  priority: RequestPriority = 'auto',
  adopt: (id: string, bytes: ArrayBuffer, type: string) => void = (id, b, t) => { void getAudioCache().adoptBytes(id, b, t) },
  holds: (id: string) => boolean = (id) => (getAudioCache().holdsClip?.(id) ?? false) || bootClipPending(id) !== null,
): Promise<void> {
  const id = audioIdOfUrl(url)
  // Already in hand, or on its way straight from S3 with the boot's first window
  // (job #298): no second download through the proxy.
  if (id && holds(id)) return Promise.resolve()
  const pending = pendingWarmups.get(url)
  if (pending) return pending
  const request = fetch(url, { priority })
    .then(async (res) => {
      if (!res.ok) return
      const bytes = await res.arrayBuffer()
      if (id) adopt(id, bytes, res.headers.get('content-type') || 'audio/mpeg')
    })
    .catch(() => {})
    .finally(() => { pendingWarmups.delete(url) })
  // Keep the reservation through body consumption and adoption. Once settled,
  // the cache decides whether another warm-up is needed (including retries).
  pendingWarmups.set(url, request)
  return request
}
